import {
  useEffect,
  useEffectEvent,
  useRef,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
} from 'react'

interface ModalDialogOptions {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Focused when the dialog opens. Falls back to the dialog itself. */
  initialFocusRef?: RefObject<HTMLElement | null> | undefined
  /**
   * Where focus goes on close when the element that opened the dialog is no
   * longer in the document — a trigger that re-rendered while it was up.
   */
  returnFocus?: (() => HTMLElement | null) | undefined
}

/**
 * Every open modal dialog, innermost last. Escape and the scrim belong to the
 * top one only: a confirm raised over an editor must close alone, and every
 * instance hears the same document keydown.
 */
const openStack: HTMLDialogElement[] = []

/**
 * The nearest focusable ancestor of wherever the pointer last went down,
 * anywhere in the document — one value, not per-dialog, because a trigger
 * lives outside the dialog it opens and this hook has no other way to reach
 * it.
 *
 * Exists for one gap: Safari does not focus a plain `<button>` on click (Chrome
 * and Firefox do), so `document.activeElement` at `showModal()` time is often
 * `<body>` there even though a trigger was tapped a moment ago, and the
 * existing opener-tracking below silently returns focus to nothing instead of
 * back to the trigger. A keyboard open never needs this — Enter/Space on a
 * focused trigger leaves it as `document.activeElement` in every engine — so
 * this is read only as that one fallback, never preferred over it.
 */
let lastPointerDownTarget: HTMLElement | null = null
let pointerDownListenerCount = 0

const FOCUSABLE_SELECTOR = 'button, a[href], input, select, textarea, [role="button"], [tabindex]'

const onDocumentPointerDown = (event: globalThis.PointerEvent) => {
  lastPointerDownTarget =
    event.target instanceof Element
      ? (event.target.closest<HTMLElement>(FOCUSABLE_SELECTOR) ?? null)
      : null
}

/** Shared across every mounted dialog — ref-counted so exactly one listener runs. */
function useLastPointerDownTarget() {
  useEffect(() => {
    if (pointerDownListenerCount === 0) {
      document.addEventListener('pointerdown', onDocumentPointerDown, true)
    }
    pointerDownListenerCount += 1
    return () => {
      pointerDownListenerCount -= 1
      if (pointerDownListenerCount === 0) {
        document.removeEventListener('pointerdown', onDocumentPointerDown, true)
      }
    }
  }, [])
}

/**
 * What Tab would visit inside `dialog`, in the order it would visit them.
 *
 * DOM order, which is also tab order here: a Popover or Tooltip opened inside
 * a dialog is portalled INTO it and promoted with `popover="manual"`, which
 * keeps its place in the sequence, so its fields count as the dialog's own.
 * Positive `tabindex` is not honoured — the app sets none.
 */
function tabbableIn(dialog: HTMLDialogElement) {
  const candidates = dialog.querySelectorAll<HTMLElement>(
    'a[href], button, input, select, textarea, summary, [tabindex], [contenteditable="true"]',
  )
  return Array.from(candidates).filter((element) => {
    if (element.tabIndex < 0) return false
    if ('disabled' in element && element.disabled === true) return false
    if (element.closest('[inert]') !== null) return false
    // A radio group is one stop: Tab lands on its checked member, or on its
    // first when none is checked, and arrows move within it.
    if (element instanceof HTMLInputElement && element.type === 'radio' && element.name !== '') {
      const group = Array.from(
        dialog.querySelectorAll<HTMLInputElement>('input[type="radio"]'),
      ).filter((radio) => radio.name === element.name && !radio.disabled)
      const stop = group.find((radio) => radio.checked) ?? group[0]
      if (stop !== element) return false
    }
    // jsdom has no layout and no `checkVisibility`; it treats all as shown.
    return typeof element.checkVisibility === 'function'
      ? element.checkVisibility({ visibilityProperty: true })
      : true
  })
}

function isOnBackdrop(dialog: HTMLDialogElement | null, event: MouseEvent | PointerEvent) {
  // A click whose target IS the dialog either hit the `::backdrop` or the
  // panel's own padding — only the rect tells the two apart.
  if (dialog === null || event.target !== dialog) return false
  const rect = dialog.getBoundingClientRect()
  return (
    event.clientX < rect.left ||
    event.clientX > rect.right ||
    event.clientY < rect.top ||
    event.clientY > rect.bottom
  )
}

/**
 * Tab past the last stop lands on the first, Shift+Tab before the first on
 * the last.
 *
 * `showModal()` is meant to do this itself, and does not: measured in
 * Chromium, Tab from the last stop and Shift+Tab from the first put focus on
 * `<body>` for one keystroke before the next Tab pulls it back in — a lap
 * around the ring drops a screen reader out of the dialog every time.
 *
 * Only the edges are taken; every other Tab keeps its default, so the browser
 * still owns the order in between. Focus on the panel itself (the landing
 * when no field is named) goes forward by default and back to the last stop.
 * Focus on a `tabIndex={-1}` menu row is left alone: its menu's own Tab
 * handler closes it and moves on from the trigger. Focus that got out anyway
 * is pulled back to the first stop, or the last on Shift+Tab.
 */
function wrapTab(dialog: HTMLDialogElement, event: KeyboardEvent) {
  const stops = tabbableIn(dialog)
  const first = stops[0]
  const last = stops.at(-1)
  const active = document.activeElement

  if (first === undefined || last === undefined) {
    // Nothing to move to: hold focus where it is rather than let it leave.
    event.preventDefault()
    return
  }
  if (!dialog.contains(active)) {
    event.preventDefault()
    ;(event.shiftKey ? last : first).focus()
    return
  }
  if (event.shiftKey && (active === first || active === dialog)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

/**
 * The behaviour of a native `<dialog>` opened with `showModal()`, synced to
 * React state: the element brings the focus trap, `inert` on everything behind
 * it and the top layer; this adds what it gets wrong or leaves out — the
 * focus trap's edges among them.
 *
 * Shared by Modal and Sheet so a fix to any of it lands in both.
 */
export function useModalDialog({
  open,
  onOpenChange,
  initialFocusRef,
  returnFocus,
}: ModalDialogOptions) {
  const dialogRef = useRef<HTMLDialogElement | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(false)
  const pressedBackdropRef = useRef(false)

  useLastPointerDownTarget()

  // An effect event, not a dependency: callers pass an inline function, and
  // as a dependency it would re-run the open path — and its `focus()` — on
  // every render, yanking focus out of whatever field the member is typing in.
  const fallbackFocus = useEffectEvent(() => returnFocus?.() ?? null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return

    if (!open) {
      // `open` is React's state; showing modally is a DOM mode only these two
      // methods can enter. Rendering the `open` attribute instead would draw
      // the panel without the modal behaviour that is the reason for the tag.
      if (dialog.open) dialog.close()

      // `close()` hands focus back on its own, but to a node that may have
      // re-rendered away, and then focus drops to <body> — from where a
      // keyboard member has to tab the document from the top. Only on a real
      // open → closed edge: on mount <body> holds focus legitimately.
      if (wasOpenRef.current && document.activeElement === document.body) {
        const opener = openerRef.current
        const target = opener?.isConnected ? opener : fallbackFocus()
        target?.focus()
      }
      wasOpenRef.current = false
      return
    }

    if (!dialog.open) {
      const active = document.activeElement
      // See `useLastPointerDownTarget` above: `<body>` here means Safari
      // never focused the trigger it was clicked or tapped through.
      openerRef.current =
        active instanceof HTMLElement && active !== document.body ? active : lastPointerDownTarget
      dialog.showModal()
    }
    wasOpenRef.current = true

    const landing = initialFocusRef?.current ?? dialog
    landing.focus()
  }, [open, initialFocusRef])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!open || dialog === null) return
    openStack.push(dialog)
    return () => {
      openStack.splice(openStack.indexOf(dialog), 1)
    }
  }, [open])

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return

    // Every way the element closes itself ends at its own `close`, so the
    // caller's state is synced from there rather than from each path.
    const sync = () => {
      onOpenChange(false)
    }
    dialog.addEventListener('close', sync)
    return () => {
      dialog.removeEventListener('close', sync)
    }
  }, [onOpenChange])

  useEffect(() => {
    if (!open) return

    // `<dialog>` answers Escape on its own — unless something inside spends
    // the key first: Chrome gives `input[type="search"]` Escape to clear
    // itself with, and the close request is never made.
    //
    // Bubble phase, not capture, and a claimed key is left alone: Popover
    // claims Escape in capture, so a menu open inside a modal closes first
    // and the modal stays.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      const dialog = dialogRef.current
      if (dialog === null || openStack.at(-1) !== dialog) return

      if (event.key === 'Tab') {
        wrapTab(dialog, event)
        return
      }
      if (event.key !== 'Escape') return
      // Claims the key outright: without this the element's own close request
      // can still fire behind us, and the dialog closes down two paths.
      event.preventDefault()
      onOpenChange(false)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) return

    // A modal dialog makes the page inert, not unscrollable: a wheel or a drag
    // over the scrim still moves the document behind it.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  const backdropProps = {
    // Both ends of the press must be on the scrim. A drag that selects text in
    // a field and is released past the panel's edge is a click on the dialog
    // too, and closing on it throws away whatever was being typed.
    onPointerDown: (event: PointerEvent<HTMLDialogElement>) => {
      pressedBackdropRef.current = isOnBackdrop(dialogRef.current, event)
    },
    onClick: (event: MouseEvent<HTMLDialogElement>) => {
      const pressed = pressedBackdropRef.current
      pressedBackdropRef.current = false
      if (pressed && isOnBackdrop(dialogRef.current, event)) onOpenChange(false)
    },
  }

  return { dialogRef, backdropProps }
}
